import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { Thumbnail, type ThumbnailProps } from '@pkg/components';

import { AssetCommonMaxWidth } from '@app/client';

import { TickStrokePath } from '../../../../clipPaths';
import { type TickStatus } from '../../../../types';
import { StatusGradient } from '../../../StatusGradient';
import { Pocket } from '../Pocket';

import { Action, FocusRing, StyledTick, ThumbnailWrapper } from './styles';

// `thumbnail` only supports `imageUrl` for now. We will wait to
// see what overall performance is like before considering `video`.
export interface TickProps {
    id: string;
    thumbnail?: ThumbnailProps['imageUrl'];
    status?: TickStatus;
    pocketCount?: number;
    active?: boolean;
    highlight?: boolean;
    // TODO: Since we do not yet have a `Popover`,
    // we will need to support `url/external` props.
    onAction?(): void;
}

export function Tick({
    id,
    thumbnail,
    status,
    pocketCount = 0,
    active = false,
    highlight = false,
    onAction,
}: TickProps) {
    // This technique is used so that we don't load all images
    // before the user has ever interacted with the <Tick />.
    // Worth investigating a good solution for optimistic image loading.
    const [hoveredOnce, setHoveredOnce] = useState(false);

    const statusGradientMarkup = active ? <StatusGradient status={status} /> : null;

    // TODO: If we later support `onActive` for `active` ticks,
    // we should just render the `focusRing` all the time.
    const focusRingMarkup = active ? null : (
        <FocusRing>
            <TickStrokePath />
        </FocusRing>
    );

    const pocketMarkup = pocketCount > 0 ? <Pocket count={pocketCount} status={status} active={active} /> : null;

    // For now, we are disabling any `active` Ticks. In the future,
    // we may want to also have the `active` Ticks be interactive.
    // If so, we will have to provide special treatment for
    // an interactive "pocket" Tick.
    const isDisabled = Boolean(active || pocketMarkup);
    const renderThumbnail = !isDisabled && (hoveredOnce || highlight);

    const thumbnailMarkup = renderThumbnail ? (
        <ThumbnailWrapper>
            <Thumbnail imageUrl={thumbnail} mashWidth={AssetCommonMaxWidth.Nft} />
        </ThumbnailWrapper>
    ) : null;

    return (
        <StyledTick id={id} $disabled={isDisabled} $highlight={highlight} custom={active}>
            <Action
                disabled={isDisabled}
                onClick={onAction}
                onMouseEnter={hoveredOnce ? undefined : () => setHoveredOnce(true)}
            >
                <AnimatePresence initial={false}>{statusGradientMarkup}</AnimatePresence>

                {thumbnailMarkup}
                {focusRingMarkup}
            </Action>

            <AnimatePresence initial={false}>{pocketMarkup}</AnimatePresence>
        </StyledTick>
    );
}
